import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import HeroSection from '../components/ui/HeroSection';
import Section from '../components/ui/Section';
import Button from '../components/ui/Button';
import { 
  AwardIcon, 
  HotelIcon, 
  CustomerServiceIcon, 
  TeamIcon, 
  ContactsIcon,
  HomeIcon,
  AuctionIcon,
  MapPinIcon,
  EventIcon,
  ExperienceIcon
} from '../utils/IconWrapper';
import { useLanguage } from '../i18n/LanguageContext';
import HeaderBanner from '../components/ui/HeaderBanner';

// Immagini temporanee da sostituire con immagini reali quando disponibili
const heroImage = 'https://images.unsplash.com/photo-1560448204-603b3fc33ddc?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1350&q=80';
const aboutImage = 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1350&q=80';

// Immagini per i servizi
const shortTermRentalImage = 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1350&q=80';
const longTermRentalImage = 'https://images.unsplash.com/photo-1484154218962-a197022b5858?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1350&q=80';
const auctionsImage = 'https://geauction.com/wp-content/uploads/2018/06/Your-First-Auction-What-You-Need-to-Know.jpg';
const buyAndSellImage = 'https://images.unsplash.com/photo-1560448204-603b3fc33ddc?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1350&q=80';
const eventsImage = 'https://images.unsplash.com/photo-1511795409834-ef04bbd61622?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80';
const experiencesImage = 'https://www.dailymood.it/wp-content/uploads/2018/04/home-35.jpg';

const AboutRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const AboutImage = styled.div`
  position: relative;
  height: 500px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
    transition: transform 0.5s ease, box-shadow 0.5s ease;
    
    &:hover {
      transform: scale(1.02);
      box-shadow: 0 15px 40px rgba(0, 0, 0, 0.3);
    }
  }
  
  &:after {
    content: '';
    position: absolute;
    bottom: -20px;
    right: -20px;
    width: 80%;
    height: 80%;
    border: 2px solid var(--accent-primary);
    z-index: -1;
    border-radius: 8px;
  }
  
  @media (max-width: 992px) {
    height: 400px;
  }
`;

const AboutContent = styled.div`
  padding: 2rem 0;
`;

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 4rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled(motion.div)`
  text-align: center;
  padding: 0;
  background-color: var(--bg-secondary);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.25);
  }
`;

const ValueImage = styled.div<{ backgroundImage: string }>`
  width: 100%;
  height: 180px;
  background-image: url(${props => props.backgroundImage});
  background-size: cover;
  background-position: center;
`;

const ValueContent = styled.div`
  padding: 1.5rem;
`;

const ValueIcon = styled.div`
  font-size: 2.8rem;
  color: var(--accent-primary);
  margin-bottom: 1.5rem;
  opacity: 0.9;
  transition: opacity 0.3s ease, transform 0.3s ease;
  
  ${ValueCard}:hover & {
    opacity: 1;
    transform: scale(1.1);
  }
`;

const ValueTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 2px;
    background-color: var(--accent-primary);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  ${ValueCard}:hover &:after {
    opacity: 1;
  }
`;

const ValueDescription = styled.p`
  color: var(--text-secondary);
  font-size: 0.95rem;
  line-height: 1.7;
  margin-top: 1rem;
`;

const StatsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2.5rem;
  padding: 2rem;
  background: linear-gradient(rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.06));
  border-radius: 12px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const StatItem = styled(motion.div)`
  text-align: center;
  padding: 1.5rem;
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.03);
  transition: transform 0.3s ease, background-color 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    background-color: rgba(255, 255, 255, 0.06);
  }
`;

const StatNumber = styled.div`
  font-size: 3.5rem;
  font-weight: 700;
  color: var(--accent-primary);
  margin-bottom: 0.8rem;
  line-height: 1;
  background: linear-gradient(135deg, var(--accent-primary), #f5a623);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const StatLabel = styled.div`
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: var(--text-secondary);
  font-weight: 500;
`;

const About: React.FC = () => {
  const { t, language } = useLanguage();
  
  return (
    <>
      <HeaderBanner />
      
      <HeroSection
        title={t('about.title')}
        backgroundImage={heroImage}
        height="70vh"
      />
      
      <Section padding="8rem 0">
        <AboutRow>
          <AboutImage>
            <img src={aboutImage} alt={t('about.alt.image')} />
          </AboutImage>
          
          <AboutContent>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              {t('about.our_story')}
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: '1.7' }}
            >
              {t('about.story.paragraph1')}
            </motion.p>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: '1.7' }}
            >
              {t('about.story.paragraph2')}
            </motion.p>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              style={{ color: 'var(--text-secondary)', marginBottom: '2rem', lineHeight: '1.7' }}
            >
              {t('about.story.paragraph3')}
            </motion.p>
          </AboutContent>
        </AboutRow>
      </Section>
      
      <Section
        title={t('about.our_departments')}
        subtitle={t('about.values.subtitle')}
        padding="8rem 0"
        centered={true}
      >
        <ValuesGrid>
          <ValueCard>
            <ValueImage backgroundImage={shortTermRentalImage} />
            <ValueContent>
              <ValueIcon>
                <HotelIcon />
              </ValueIcon>
              <ValueTitle>{language === 'it' ? 'Affitti Brevi' : 'Short-Term Rentals'}</ValueTitle>
              <ValueDescription>
                {language === 'it' ? 'Offriamo alloggi a breve termine di lusso con servizi premium. Ogni proprietà è selezionata per garantire comfort, stile e un\'esperienza impeccabile.' : 'We offer luxury short-term accommodations with premium services. Each property is selected to ensure comfort, style, and an impeccable experience.'}
              </ValueDescription>
            </ValueContent>
          </ValueCard>
          
          <ValueCard>
            <ValueImage backgroundImage={longTermRentalImage} />
            <ValueContent>
              <ValueIcon>
                <HomeIcon />
              </ValueIcon>
              <ValueTitle>{language === 'it' ? 'Affitti Medio/Lungo Termine' : 'Medium/Long-Term Rentals'}</ValueTitle>
              <ValueDescription>
                {language === 'it' ? 'Soluzioni abitative completamente arredate per soggiorni prolungati, ideali per trasferimenti di lavoro o per chi desidera vivere un\'esperienza in una nuova località.' : 'Fully furnished housing solutions for extended stays, ideal for work relocations or for those who want to experience living in a new location.'}
              </ValueDescription>
            </ValueContent>
          </ValueCard>
          
          <ValueCard>
            <ValueImage backgroundImage={auctionsImage} />
            <ValueContent>
              <ValueIcon>
                <AuctionIcon />
              </ValueIcon>
              <ValueTitle>{language === 'it' ? 'Aste Immobiliari' : 'Real Estate Auctions'}</ValueTitle>
              <ValueDescription>
                {language === 'it' ? 'Il nostro team di esperti offre consulenza pre-asta, assistenza durante la procedura e supporto post-aggiudicazione per accedere a opportunità immobiliari esclusive.' : 'Our team of experts offers pre-auction consulting, assistance during the procedure, and post-award support to access exclusive real estate opportunities.'}
              </ValueDescription>
            </ValueContent>
          </ValueCard>
          
          <ValueCard>
            <ValueImage backgroundImage={buyAndSellImage} />
            <ValueContent>
              <ValueIcon>
                <MapPinIcon />
              </ValueIcon>
              <ValueTitle>{language === 'it' ? 'Compravendite' : 'Buy and Sell'}</ValueTitle>
              <ValueDescription>
                {language === 'it' ? 'Servizi completi di consulenza immobiliare per acquirenti e venditori, dalla valutazione iniziale alla negoziazione e finalizzazione della transazione.' : 'Complete real estate consulting services for buyers and sellers, from initial valuation to negotiation and transaction finalization.'}
              </ValueDescription>
            </ValueContent>
          </ValueCard>
          
          <ValueCard>
            <ValueImage backgroundImage={eventsImage} />
            <ValueContent>
              <ValueIcon>
                <EventIcon />
              </ValueIcon>
              <ValueTitle>{language === 'it' ? 'Eventi' : 'Events'}</ValueTitle>
              <ValueDescription>
                {language === 'it' ? 'Trasformiamo le nostre location esclusive in palcoscenici per eventi privati e aziendali, occupandoci di ogni dettaglio dalla pianificazione all\'esecuzione.' : 'We transform our exclusive locations into stages for private and corporate events, taking care of every detail from planning to execution.'}
              </ValueDescription>
            </ValueContent>
          </ValueCard>
          
          <ValueCard>
            <ValueImage backgroundImage={experiencesImage} />
            <ValueContent>
              <ValueIcon>
                <ExperienceIcon />
              </ValueIcon>
              <ValueTitle>{language === 'it' ? 'Servizi/Experience' : 'Services/Experience'}</ValueTitle>
              <ValueDescription>
                {language === 'it' ? 'Esperienze personalizzate che arricchiscono il soggiorno, dai tour enogastronomici alle lezioni di cucina, dalle escursioni private alle lezioni di italiano.' : 'Personalized experiences that enrich your stay, from food and wine tours to cooking classes, from private excursions to Italian lessons.'}
              </ValueDescription>
            </ValueContent>
          </ValueCard>
        </ValuesGrid>
      </Section>
      
      <Section
        title={t('about.our_values')}
        subtitle={t('about.values.subtitle')}
        backgroundColor="var(--bg-secondary)"
        padding="8rem 0"
        centered={true}
      >
        <ValuesGrid>
          <ValueCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <ValueIcon>
              <AwardIcon />
            </ValueIcon>
            <ValueTitle>{t('about.values.excellence.title')}</ValueTitle>
            <ValueDescription>
              {t('about.values.excellence.description')}
            </ValueDescription>
          </ValueCard>
          
          <ValueCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <ValueIcon>
              <HotelIcon />
            </ValueIcon>
            <ValueTitle>{t('about.values.luxury.title')}</ValueTitle>
            <ValueDescription>
              {t('about.values.luxury.description')}
            </ValueDescription>
          </ValueCard>
          
          <ValueCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <ValueIcon>
              <CustomerServiceIcon />
            </ValueIcon>
            <ValueTitle>{t('about.values.service.title')}</ValueTitle>
            <ValueDescription>
              {t('about.values.service.description')}
            </ValueDescription>
          </ValueCard>
          
          <ValueCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <ValueIcon>
              <TeamIcon />
            </ValueIcon>
            <ValueTitle>{t('about.values.integrity.title')}</ValueTitle>
            <ValueDescription>
              {t('about.values.integrity.description')}
            </ValueDescription>
          </ValueCard>
          
          <ValueCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
          >
            <ValueIcon>
              <ContactsIcon />
            </ValueIcon>
            <ValueTitle>{t('about.values.community.title')}</ValueTitle>
            <ValueDescription>
              {t('about.values.community.description')}
            </ValueDescription>
          </ValueCard>
          
          <ValueCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.0 }}
          >
            <ValueIcon>
              <AwardIcon />
            </ValueIcon>
            <ValueTitle>{t('about.values.innovation.title')}</ValueTitle>
            <ValueDescription>
              {t('about.values.innovation.description')}
            </ValueDescription>
          </ValueCard>
        </ValuesGrid>
      </Section>
      
      <Section
        title={t('about.our_impact')}
        centered={true}
        padding="8rem 0"
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            duration: 0.8,
            ease: [0.25, 0.1, 0.25, 1.0]
          }}
        >
          <StatsSection>
            <StatItem
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              whileHover={{ y: -10, transition: { duration: 0.2 } }}
            >
              <StatNumber>200+</StatNumber>
              <StatLabel>{t('about.properties_managed')}</StatLabel>
            </StatItem>
            
            <StatItem
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              whileHover={{ y: -10, transition: { duration: 0.2 } }}
            >
              <StatNumber>5000+</StatNumber>
              <StatLabel>{t('about.happy_clients')}</StatLabel>
            </StatItem>
            
            <StatItem
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              whileHover={{ y: -10, transition: { duration: 0.2 } }}
            >
              <StatNumber>15</StatNumber>
              <StatLabel>{t('about.cities_covered')}</StatLabel>
            </StatItem>
            
            <StatItem
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.7 }}
              whileHover={{ y: -10, transition: { duration: 0.2 } }}
            >
              <StatNumber>13</StatNumber>
              <StatLabel>{t('about.years_experience')}</StatLabel>
            </StatItem>
          </StatsSection>
        </motion.div>
      </Section>
      
      <Section
        backgroundColor="var(--bg-secondary)"
        padding="6rem 0"
        centered={true}
        backgroundImage="https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
        blurredBackground={true}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ 
            duration: 0.7, 
            ease: [0.25, 0.1, 0.25, 1.0],
            delay: 0.2
          }}
          style={{ 
            maxWidth: '700px', 
            margin: '0 auto', 
            textAlign: 'center',
            padding: '3rem',
            backgroundColor: 'rgba(15, 15, 15, 0.7)',
            backdropFilter: 'blur(8px)',
            borderRadius: '16px',
            boxShadow: '0 15px 35px rgba(0, 0, 0, 0.2)'
          }}
        >
          <motion.h2 
            style={{ marginBottom: '1.5rem' }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            {t('about.ready_for_luxury')}
          </motion.h2>
          <motion.p 
            style={{ 
              color: 'var(--text-secondary)', 
              marginBottom: '2.5rem',
              lineHeight: '1.8',
              fontSize: '1.1rem'
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            {t('about.ready_for_luxury_text')}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.8 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              variant="primary"
              size="large"
              to="/contact"
              style={{ 
                padding: '0.8rem 2.5rem',
                borderRadius: '30px',
                fontSize: '1.1rem',
                fontWeight: '500',
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)'
              }}
            >
              {t('contact.title')}
            </Button>
          </motion.div>
        </motion.div>
      </Section>
    </>
  );
};

export default About; 
